import React, { useState, useMemo } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ClinicalPresentationForm = ({ 
  formData, 
  onFormChange, 
  errors 
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  const symptomDurationOptions = [
    { value: 'acute', label: 'Acute (<24 hours)' },
    { value: 'subacute', label: 'Subacute (1-7 days)' },
    { value: 'chronic', label: 'Chronic (>7 days)' },
    { value: 'recurrent', label: 'Recurrent episodes' }
  ];

  const severityOptions = [
    { value: 'mild', label: 'Mild - Minimal impact' },
    { value: 'moderate', label: 'Moderate - Significant discomfort' },
    { value: 'severe', label: 'Severe - Debilitating symptoms' }
  ];

  const commonSymptoms = [
    "Chest pain radiating to left arm",
    "Severe headache with visual disturbances",
    "Abdominal pain in right upper quadrant",
    "Shortness of breath with exertion",
    "Lower back pain with leg numbness",
    "Persistent cough with hemoptysis",
    "Sudden onset confusion and weakness",
    "Joint pain and swelling",
    "Fever with night sweats",
    "Dizziness and loss of balance",
    "Nausea and vomiting",
    "Blurred vision or vision loss",
    "Difficulty swallowing",
    "Unexplained weight loss"
  ];

  // Filter suggestions based on user input
  const filteredSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery?.length < 2) {
      return commonSymptoms;
    }
    
    const query = searchQuery?.toLowerCase();
    const matches = commonSymptoms?.filter(symptom => 
      symptom?.toLowerCase()?.includes(query)
    );
    
    return matches;
  }, [searchQuery]);

  const handleSaveComplaint = () => {
    if (formData?.chiefComplaint && formData?.chiefComplaint?.trim() !== '') {
      setIsSaved(true);
      setShowSuggestions(false);
      
      // Reset saved state after 3 seconds
      setTimeout(() => {
        setIsSaved(false);
      }, 3000);
    }
  };

  const handleInputChange = (value) => {
    setSearchQuery(value);
    onFormChange('chiefComplaint', value);
    setShowSuggestions(true);
    setIsSaved(false); // Reset saved state when user types
  };

  const handleSymptomClick = (symptom) => {
    setSearchQuery(symptom);
    onFormChange('chiefComplaint', symptom);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click events to fire
    setTimeout(() => setShowSuggestions(false), 200);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="FileText" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Clinical Presentation</h2>
          <p className="text-sm text-muted-foreground">Detailed symptom and examination findings</p>
        </div>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                label="Chief Complaint"
                type="text"
                placeholder="Start typing or select from common presentations"
                value={formData?.chiefComplaint}
                onChange={(e) => handleInputChange(e?.target?.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={handleBlur}
                error={errors?.chiefComplaint}
                required
                description="Primary symptom or reason for imaging request"
              />
            </div>
            <div className="flex items-end pb-6">
              <Button
                onClick={handleSaveComplaint}
                disabled={!formData?.chiefComplaint || formData?.chiefComplaint?.trim() === ''}
                className={`transition-all ${
                  isSaved 
                    ? 'bg-green-600 hover:bg-green-700' :'bg-accent hover:bg-accent/90'
                }`}
              >
                {isSaved ? (
                  <div className="flex items-center gap-2">
                    <Icon name="Check" size={16} />
                    <span>Saved</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Icon name="Save" size={16} />
                    <span>Save</span>
                  </div>
                )}
              </Button>
            </div>
          </div>

          {isSaved && (
            <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircle" size={16} color="#16a34a" />
                <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                  Chief complaint saved successfully
                </p>
              </div>
            </div>
          )}

          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              <div className="p-2">
                {filteredSuggestions?.length > 0 ? (
                  <>
                    <p className="text-xs text-muted-foreground px-2 py-1 font-medium">
                      {searchQuery?.length < 2 ? 'Common presentations:' : 'Matching suggestions:'}
                    </p>
                    {filteredSuggestions?.map((symptom, index) => (
                      <button
                        key={index}
                        onClick={() => handleSymptomClick(symptom)}
                        className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded transition-clinical"
                      >
                        {symptom}
                      </button>
                    ))}
                  </>
                ) : (
                  <div className="px-3 py-4 text-center">
                    <p className="text-sm text-muted-foreground mb-2">
                      No matching suggestions found
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Continue typing your custom complaint or try different keywords
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Symptom Duration"
            options={symptomDurationOptions}
            value={formData?.symptomDuration}
            onChange={(value) => onFormChange('symptomDuration', value)}
            error={errors?.symptomDuration}
            required
            placeholder="Select duration"
            description="Time since symptom onset"
          />

          <Select
            label="Symptom Severity"
            options={severityOptions}
            value={formData?.severity}
            onChange={(value) => onFormChange('severity', value)}
            error={errors?.severity}
            required
            placeholder="Select severity"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Clinical History
            <span className="text-destructive ml-1">*</span>
          </label>
          <textarea
            value={formData?.clinicalHistory}
            onChange={(e) => onFormChange('clinicalHistory', e?.target?.value)}
            placeholder="Enter relevant medical history, previous investigations, and current medications..."
            rows={4}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-clinical resize-none"
          />
          {errors?.clinicalHistory && (
            <p className="text-xs text-destructive mt-1">{errors?.clinicalHistory}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">Include relevant past medical history and current medications</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Physical Examination Findings
          </label>
          <textarea
            value={formData?.examinationFindings}
            onChange={(e) => onFormChange('examinationFindings', e?.target?.value)}
            placeholder="Enter relevant physical examination findings..."
            rows={3}
            className="w-full px-3 py-2 bg-background border border-input rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-clinical resize-none"
          />
          <p className="text-xs text-muted-foreground mt-1">Document pertinent positive and negative findings</p>
        </div>
      </div>
    </div>
  );
};

export default ClinicalPresentationForm;